import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageWrap, FadeIn } from '../components/Section';

const stats = [
  { num: '9+', label: 'Years Experience' },
  { num: 'NABL', label: 'Accredited Lab' },
  { num: '3', label: 'Testing Disciplines' },
  { num: '50+', label: 'Equipment Types' },
];

const services = [
  { icon: '⛏️', title: 'Geotechnical Investigation', desc: 'Soil testing, borehole drilling, SBC analysis, and subsurface exploration for foundation design.', path: '/geotechnical' },
  { icon: '🏗️', title: 'Material Testing', desc: 'Concrete, cement, aggregate, steel, and water testing per IS standards.', path: '/materials' },
  { icon: '⚙️', title: 'Equipment Supply', desc: 'Manufacturing and supply of calibrated civil engineering testing machines.', path: '/equipment' },
  { icon: '📐', title: 'Engineering Consultancy', desc: 'Structural analysis, slope stability modeling, and project advisory services.', path: '/about' },
];

export default function Home() {
  return (
    <PageWrap>
      {/* HERO */}
      <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', paddingTop: 100, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '40%', height: '100%', background: 'linear-gradient(135deg, transparent 30%, rgba(45,74,45,0.03) 100%)' }} />
        <div className="container" style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 60, alignItems: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: 'rgba(45,74,45,0.06)', border: '1px solid rgba(45,74,45,0.12)', borderRadius: 24, fontSize: '0.78rem', fontWeight: 500, color: 'var(--primary)', letterSpacing: 0.5, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, background: 'var(--primary)', borderRadius: '50%', animation: 'pulse 2s infinite' }} /> NABL Accredited · ISO/IEC 17025:2017
            </div>
            <h1 style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)', fontWeight: 700, marginBottom: 24, color: 'var(--primary-dark)' }}>
              Geotechnical Investigation &<br />
              <span style={{ color: 'var(--primary-light)' }}>Materials Testing Laboratory</span>
            </h1>
            <p style={{ fontSize: '1.08rem', lineHeight: 1.7, color: 'var(--text-mid)', marginBottom: 36, maxWidth: 540 }}>
              Central India's premier civil engineering testing lab and research centre. NABL-certified soil testing, construction material analysis, and equipment supply — headquartered in Bhopal, Madhya Pradesh.
            </p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn btn-primary">Request Testing Quote</Link>
              <a href="/documents/nabl-certificate.pdf" target="_blank" rel="noopener noreferrer" className="btn btn-outline">Download NABL Certificate</a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.4 }}
            style={{ display: 'flex', justifyContent: 'center' }} className="hero-card-wrap">
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 20, padding: 32, width: '100%', maxWidth: 340, boxShadow: '0 8px 40px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                <span style={{ padding: '4px 10px', background: 'var(--warm)', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, color: 'var(--primary)' }}>TC-12248</span>
                <span style={{ padding: '4px 10px', background: 'rgba(45,140,45,0.1)', color: '#2d8c2d', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600 }}>Active</span>
              </div>
              <h3 style={{ fontSize: '1.3rem', marginBottom: 4 }}>NABL Accreditation</h3>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-light)', marginBottom: 20 }}>ISO/IEC 17025:2017</p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {['Mechanical', 'Chemical', 'NDT'].map(d => (
                  <span key={d} style={{ padding: '6px 12px', background: 'var(--cream)', borderRadius: 4, fontSize: '0.78rem', fontWeight: 500, color: 'var(--primary)' }}>{d}</span>
                ))}
              </div>
              <div style={{ paddingTop: 16, borderTop: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text-mid)' }}>
                Valid through September 2029
              </div>
            </div>
          </motion.div>
        </div>
        <style>{`
          @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
          @media(max-width:900px){ .hero-card-wrap{display:none !important} .container{grid-template-columns:1fr !important} }
        `}</style>
      </section>

      {/* STATS */}
      <section style={{ padding: '60px 0', background: 'var(--primary-dark)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 32, textAlign: 'center' }}>
            {stats.map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: '2.5rem', fontWeight: 700, color: 'var(--accent)' }}>{s.num}</div>
                <div style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section style={{ padding: '100px 0', background: 'var(--white)' }}>
        <div className="container">
          <FadeIn>
            <h2 style={{ fontSize: '2.4rem', marginBottom: 12 }}>Our Core Services</h2>
            <p style={{ fontSize: '1.05rem', color: 'var(--text-light)', maxWidth: 560, marginBottom: 48 }}>
              Comprehensive geotechnical investigation and material testing for infrastructure, construction, and research.
            </p>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {services.map((s, i) => (
              <FadeIn key={s.title} delay={i * 0.1}>
                <Link to={s.path} style={{ display: 'block', padding: 36, background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', transition: 'all 0.3s', position: 'relative' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <span style={{ fontSize: '2rem', display: 'block', marginBottom: 16 }}>{s.icon}</span>
                  <h3 style={{ fontSize: '1.2rem', marginBottom: 10 }}>{s.title}</h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-mid)', lineHeight: 1.6 }}>{s.desc}</p>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* MANIT HIGHLIGHT */}
      <section style={{ padding: '100px 0', background: 'var(--warm)' }}>
        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 60, alignItems: 'center' }}>
          <FadeIn>
            <span style={{ display: 'inline-block', padding: '6px 14px', background: 'rgba(201,169,97,0.15)', color: 'var(--accent)', borderRadius: 4, fontSize: '0.8rem', fontWeight: 600, marginBottom: 16 }}>Landmark Project</span>
            <h2 style={{ fontSize: '2.2rem', marginBottom: 16 }}>Indian Railways Infrastructure</h2>
            <p style={{ fontSize: '1rem', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 24 }}>
              Partnered with MANIT Bhopal on Design Analysis of Slope Stability for the Mathura–Jhansi 3rd Line Project — advanced geotechnical modeling and in-situ shear testing for Indian Railways.
            </p>
            <ul style={{ listStyle: 'none', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 32 }}>
              {['Slope stability modeling', 'In-situ shear testing', 'Geotechnical analysis', 'MANIT collaboration'].map(item => (
                <li key={item} style={{ fontSize: '0.9rem', color: 'var(--text-mid)', paddingLeft: 20, position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: 'var(--primary)', fontWeight: 700 }}>✓</span>{item}
                </li>
              ))}
            </ul>
            <Link to="/about" className="btn btn-primary">Learn More</Link>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 28 }}>
                <div style={{ fontSize: '1.8rem', marginBottom: 12 }}>🎓</div>
                <h4 style={{ fontSize: '1.1rem', marginBottom: 4 }}>MANIT Bhopal</h4>
                <p style={{ fontSize: '0.88rem', color: 'var(--text-mid)' }}>Research Partnership</p>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>Maulana Azad National Institute of Technology</span>
              </div>
              <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 28 }}>
                <div style={{ fontSize: '1.8rem', marginBottom: 12 }}>🚂</div>
                <h4 style={{ fontSize: '1.1rem', marginBottom: 4 }}>Indian Railways</h4>
                <p style={{ fontSize: '0.88rem', color: 'var(--text-mid)' }}>Mathura–Jhansi 3rd Line</p>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>Package 1 & 2 — Slope Stability</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* DUAL MODEL */}
      <section style={{ padding: '100px 0', background: 'var(--white)' }}>
        <div className="container">
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{ fontSize: '2.2rem', marginBottom: 12 }}>A Unique Dual Model</h2>
              <p style={{ fontSize: '1rem', color: 'var(--text-light)', maxWidth: 520, margin: '0 auto' }}>
                We don't just test — we manufacture. Our calibrated equipment is used in our own NABL-accredited lab daily.
              </p>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 32 }}>
            <FadeIn delay={0.1}>
              <div style={{ padding: '48px 40px', background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 20 }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: '3rem', fontWeight: 700, color: 'var(--border)', marginBottom: 16 }}>01</div>
                <h3 style={{ fontSize: '1.3rem', marginBottom: 12 }}>Accredited Testing Lab</h3>
                <p style={{ fontSize: '0.92rem', color: 'var(--text-mid)', lineHeight: 1.7 }}>NABL-certified testing across mechanical, chemical, and NDT disciplines. Results accepted by government agencies and auditors.</p>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div style={{ padding: '48px 40px', background: 'var(--primary-dark)', borderRadius: 20 }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: '3rem', fontWeight: 700, color: 'var(--accent)', marginBottom: 16 }}>02</div>
                <h3 style={{ fontSize: '1.3rem', marginBottom: 12, color: 'white' }}>Equipment Manufacturer</h3>
                <p style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>We supply calibrated testing machines to labs across Central India. Every machine we sell is proven in our own daily operations.</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <FadeIn>
            <div style={{ background: 'var(--primary)', borderRadius: 20, padding: 'clamp(40px, 6vw, 64px)', textAlign: 'center' }}>
              <h2 style={{ fontSize: '2.2rem', color: 'white', marginBottom: 16 }}>Ready to Start Your Project?</h2>
              <p style={{ fontSize: '1.02rem', color: 'rgba(255,255,255,0.8)', maxWidth: 500, margin: '0 auto 32px' }}>
                From soil investigation to material testing, our team delivers accurate, NABL-certified results on schedule.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn btn-accent">Get a Quote</Link>
                <Link to="/geotechnical" className="btn btn-outline" style={{ borderColor: 'white', color: 'white' }}>Explore Services</Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </PageWrap>
  );
}
